import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Achievement, LinkType, meGQL } from "@onmo/graphql/graphql";
import { find } from "lodash-es";
import { ActiveIframe } from "../../components/common/GameFeed/ActiveIframe";
import { RewardScreen } from "../../components/common/RewardScreen";
import { useAppDispatch, useAppSelector } from "../../components/hooks";
import { useWalletConfig } from "../../hooks/useWalletConfig";
import { getWalletBalance } from "../../constants/wallet";
import { PageRoutes } from "../../constants/historyGaming";
import { useStyles } from "./PlayIframe.style";
import { serviceLinkMap } from "../../hooks/useDirectoryData";
import { setIsShowService } from "../../store/slices/popup";
import { useFeaturedCards } from "../../hooks/useFeaturedCards";
import { HomepageMd } from "../../models/homepage";
import { shouldRedirectExternal } from "../../models/theme";
import { withTgpSource } from "../../models/externalLink";
import { FEATURES } from "../../types/theme";
import {
  clearPreviousAchievements,
  clearPreviousGzCoin,
  getPreviousAchievements,
  getPreviousGzCoin,
} from "../../models/storage";
import { useExternalLaunchSnapshot } from "../../hooks/useExternalLaunchSnapshot";

export const PlayIframe = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { theme } = useAppSelector((state) => state.theme);
  const { data: dataMe, refetch } = useQuery(meGQL);
  const storeLaunchSnapshot = useExternalLaunchSnapshot();
  const { walletConfig } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;
  const [activeIframe, setActiveIframe] = useState("");
  const [rewardAmount, setRewardAmount] = useState(0);
  const [restoredAchievements, setRestoredAchievements] = useState<
    Achievement[] | null
  >(null);
  const oldGzCoinRef = useRef<number | null>(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const feedId = params.get("feedId");
  const category = params.get("category");
  const { homeFeed } = useFeaturedCards();
  const hideRewardPage =
    theme?.hideFeature?.includes(FEATURES.rewardPage) || false;
  const isReturnPlatform = params.get("returnPlatform") === "true";
  const navigate = useNavigate();

  const backToHomePage = () => {
    navigate(PageRoutes.HomePage, { replace: true });
    dispatch(setIsShowService({ isShowService: false }));
    clearPreviousGzCoin();
  };

  const handleCloseIframe = async () => {
    setActiveIframe("");
    if (hideRewardPage) {
      backToHomePage();
      return;
    }
    const userUpdate = await refetch();
    if (userUpdate?.data?.me) {
      const oldGzCoin = oldGzCoinRef.current ?? 0;
      const newGzCoin = getWalletBalance(
        userUpdate?.data?.me?.coinWallets ?? [],
        baseCoinType,
      );
      if (oldGzCoin !== newGzCoin) {
        setRewardAmount(newGzCoin - oldGzCoin);
      } else {
        backToHomePage();
      }
    }
  };

  useEffect(() => {
    if (category || isReturnPlatform) return;
    const gameFeed = find(homeFeed, { id: feedId });
    if (!gameFeed) return;
    const link = HomepageMd.getLink(gameFeed);
    if (
      shouldRedirectExternal(theme) &&
      gameFeed.linkType === LinkType.ExternalIframe
    ) {
      const linkWithTgpSource = withTgpSource(link);
      let cancelled = false;
      const leaveForExternalGame = async () => {
        await storeLaunchSnapshot();
        if (cancelled) return;
        window.location.replace(linkWithTgpSource);
      };
      leaveForExternalGame();

      return () => {
        cancelled = true;
      };
    }
    setActiveIframe(link);
  }, [feedId, homeFeed]);

  useEffect(() => {
    if (feedId || !category || isReturnPlatform) return;
    const categoryLink = serviceLinkMap[category];
    if (!categoryLink) return;
    setActiveIframe(categoryLink);
  }, [category]);

  useEffect(() => {
    if (oldGzCoinRef.current === null && dataMe?.me?.coinWallets) {
      oldGzCoinRef.current = getWalletBalance(
        dataMe.me.coinWallets,
        baseCoinType,
      );
    }
  }, [dataMe.me.coinWallets, isReturnPlatform]);

  // External game reward flow: if the user returns from an external game, we need to check if their GZ coin balance has changed. If it has, we show the reward screen. If not, we navigate back to the home page.
  useEffect(() => {
    if (!isReturnPlatform) return;
    const previousGzCoin = getPreviousGzCoin();
    const storedAchievements = getPreviousAchievements();
    clearPreviousGzCoin();
    clearPreviousAchievements();
    if (previousGzCoin === null || hideRewardPage) {
      backToHomePage();
      return;
    }
    const showExternalGameReward = async () => {
      const userUpdate = await refetch();
      if (!userUpdate?.data?.me) {
        backToHomePage();
        return;
      }
      const newGzCoin = getWalletBalance(
        userUpdate.data.me.coinWallets ?? [],
        baseCoinType,
      );
      if (previousGzCoin === newGzCoin) {
        backToHomePage();
        return;
      }
      setRestoredAchievements(storedAchievements);
      setRewardAmount(newGzCoin - previousGzCoin);
    };

    showExternalGameReward();
  }, [isReturnPlatform]);

  return (
    <div>
      {activeIframe && !isReturnPlatform && (
        <ActiveIframe
          iframeUrl={activeIframe}
          onCloseIframe={handleCloseIframe}
          feedId={feedId ?? ""}
        />
      )}
      {!hideRewardPage && rewardAmount !== 0 && (
        <RewardScreen
          className={classes.endScreen}
          rewardAmount={rewardAmount}
          previousAchievements={restoredAchievements}
        />
      )}
    </div>
  );
};
